/**
 * Sesion de Cognito. El ID token se renueva solo cuando hace falta (le quedan < 60 s), con una sola
 * renovacion en vuelo aunque varias llamadas la pidan a la vez. Si la renovacion falla se cierra la
 * sesion y App.vue manda al login. Se sincroniza entre pestañas con el evento `storage`.
 */
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

import {
  clearSession,
  loadSession,
  passwordSignIn,
  refreshSession,
  saveSession,
  secondsLeft,
  SESSION_KEY,
  type Session,
  userFrom,
} from "@/lib/cognito";

const REFRESH_MARGIN_S = 60;

export const useAuthStore = defineStore("auth", () => {
  const session = shallowRef<Session | null>(loadSession());
  const refreshing = ref<Promise<string | null> | null>(null);

  const isAuthenticated = computed(() => session.value !== null);
  const user = computed(() => {
    if (!session.value) return null;
    try {
      return userFrom(session.value);
    } catch {
      return null;
    }
  });

  /** Grupo Cognito `admin`: prompts globales y laboratorio (el backend lo vuelve a comprobar). */
  const isAdmin = computed(() => user.value?.isAdmin ?? false);

  function setSession(next: Session | null) {
    session.value = next;
    if (next) saveSession(next);
    else clearSession();
  }

  async function signIn(email: string, password: string, turnstileToken = ""): Promise<void> {
    setSession(await passwordSignIn(email.trim(), password, turnstileToken));
  }

  function signOut(): void {
    setSession(null);
  }

  /** ID token vigente (renovado si hace falta) o null si no hay sesion. */
  async function getIdToken(): Promise<string | null> {
    const current = session.value;
    if (!current) return null;
    if (secondsLeft(current) > REFRESH_MARGIN_S) return current.idToken;
    if (!current.refreshToken) {
      signOut();
      return null;
    }
    if (!refreshing.value) {
      refreshing.value = refreshSession(current)
        .then((next) => {
          setSession(next);
          return next.idToken;
        })
        .catch(() => {
          signOut();
          return null;
        })
        .finally(() => {
          refreshing.value = null;
        });
    }
    return refreshing.value;
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key === SESSION_KEY) session.value = loadSession();
    });
  }

  return { session, isAuthenticated, isAdmin, user, signIn, signOut, getIdToken };
});
